import React, { useState, useEffect } from 'react';
import { fetchSalesReport } from '../services/api';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Superscript reship count, e.g. 160⁺⁴. The base is the non-reship order count;
// base + exponent = the reship-inclusive total. No visible legend by design —
// hover title explains it.
function ReshipSup({ n }) {
  if (!n) return null;
  return (
    <sup
      title={`+${n} reship${n === 1 ? '' : 's'} (replacement shipments) included in the total`}
      style={{ fontSize: '0.7em', fontWeight: 700, color: 'var(--orange)', marginLeft: 1, cursor: 'help' }}
    >
      +{n}
    </sup>
  );
}

export default function SalesReport({ navigateTo }) {
  const [showVips, setShowVips] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refMonth, setRefMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const mm = String(refMonth.month).padStart(2, '0');
    const date = `${mm}/01/${refMonth.year}`;
    setLoading(true);
    setError(null);
    fetchSalesReport(date)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [refMonth]);

  const shiftMonth = (deltaMonths) => {
    setRefMonth(prev => {
      const d = new Date(prev.year, prev.month - 1 + deltaMonths, 1);
      return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });
  };

  if (error) {
    return (
      <div>
        <div className="breadcrumb"><span>Dashboard</span><span> › </span><span>Sales Report</span></div>
        <div className="page-header">Sales Report</div>
        <div style={{ marginTop: 24, padding: 20, fontSize: 13, color: '#c0392b', background: '#fff5f5', border: '1px solid #fcc', borderRadius: 6 }}>
          <strong>Error loading sales report:</strong> {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <div className="breadcrumb"><span>Dashboard</span><span> › </span><span>Sales Report</span></div>
        <div className="page-header">Sales Report</div>
        <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading sales report...</div>
      </div>
    );
  }

  const calendarCells = [];
  for (let i = 0; i < data.startDayOfWeek; i++) calendarCells.push(null);
  data.days.forEach(d => calendarCells.push(d));
  while (calendarCells.length % 7 !== 0) calendarCells.push(null);

  const weeks = [];
  for (let wi = 0; wi < calendarCells.length; wi += 7) weeks.push(calendarCells.slice(wi, wi + 7));
  const parseMoney = s => Number(String(s || '0').replace(/[$,]/g, '')) || 0;
  const fmtMoney = n => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const weekTotal = week => week.reduce((a, d) => d ? {
    base: a.base + (d.ordersBase ?? d.orders ?? 0),
    reships: a.reships + (d.reships ?? 0),
    rev: a.rev + parseMoney(d.revenue),
    vipC: a.vipC + (d.vipOrders ?? 0),
    vipRev: a.vipRev + parseMoney(d.vipRevenue),
    vipSignups: a.vipSignups + (d.vipSignups ?? 0),
  } : a, { base: 0, reships: 0, rev: 0, vipC: 0, vipRev: 0, vipSignups: 0 });

  const totalVipSignups = data.days.reduce((s, d) => s + (d.vipSignups || 0), 0);
  const totalVipNew = data.days.reduce((s, d) => s + (d.vipNew || 0), 0);
  const totalVipRejoined = data.days.reduce((s, d) => s + (d.vipRejoined || 0), 0);

  // "+N VIPs (X new · Y rejoined)" — collapses when either side is zero
  const vipSignupLabel = (signups, nw, rejoined) => {
    if (!signups) return null;
    if (!rejoined) return `+${nw} new VIP${nw === 1 ? '' : 's'}`;
    if (!nw) return `+${rejoined} VIP${rejoined === 1 ? '' : 's'} rejoined`;
    return `+${signups} VIPs (${nw} new · ${rejoined} rejoined)`;
  };

  return (
    <div>
      <div className="breadcrumb">
        <span>Dashboard</span>
        <span> › </span>
        <span>Sales Report</span>
      </div>
      <div className="page-header">Sales Report</div>
      <div style={{ marginBottom: 16 }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <input type="checkbox" checked={showVips} onChange={e => setShowVips(e.target.checked)} />
          Show VIPs
        </label>

        <div className="sales-report-nav" style={loading ? { opacity: 0.6 } : undefined}>
          <button className="nav-btn" title="Back one year" disabled={loading} onClick={() => shiftMonth(-12)}><ChevronsLeft size={13} /></button>
          <button className="nav-btn" title="Back one month" disabled={loading} onClick={() => shiftMonth(-1)}><ChevronLeft size={13} /></button>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Orders Placed</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--orange)' }}>{data.range}</span>
          <button className="nav-btn" title="Forward one month" disabled={loading} onClick={() => shiftMonth(1)}><ChevronRight size={13} /></button>
          <button className="nav-btn" title="Forward one year" disabled={loading} onClick={() => shiftMonth(12)}><ChevronsRight size={13} /></button>
          <input
            type="month"
            className="sales-report-month"
            value={`${refMonth.year}-${String(refMonth.month).padStart(2, '0')}`}
            disabled={loading}
            onChange={e => { const [y, m] = e.target.value.split('-').map(Number); if (y && m) setRefMonth({ year: y, month: m }); }}
            title="Jump to month"
          />
        </div>

        <div style={{ width: 120 }} />
      </div>

      <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>
        {data.month} &nbsp;
        <span style={{ color: 'var(--gray-500)', fontWeight: 400 }}>
          ({(data.summary.totalOrdersBase ?? data.summary.totalOrders)}<ReshipSup n={data.summary.totalReships ?? 0} /> orders · <strong>{data.summary.revenue}</strong>
          {showVips && ` / VIP: ${data.summary.vipOrders} (${data.summary.vipPct}%) – ${data.summary.vipRevenue} (${data.summary.vipRevPct}%)`})
        </span>
      </div>
      <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: 'var(--orange)', marginBottom: 16 }}>
        +{totalVipSignups} VIP signup{totalVipSignups === 1 ? '' : 's'}
        <span style={{ fontWeight: 400 }}> · {totalVipNew} new · {totalVipRejoined} rejoined</span>
      </div>

      <div className="calendar-grid has-week-col">
        {DAYS.map(d => (
          <div key={d} className="calendar-day-header">{d}</div>
        ))}
        <div className="calendar-day-header">Week</div>
        {weeks.map((week, wi) => {
          const wt = weekTotal(week);
          return (
            <React.Fragment key={wi}>
              {week.map((day, i) => (
                <div key={i} className={`calendar-day ${!day ? 'empty' : ''}`}
                  onClick={day && navigateTo ? () => navigateTo('shipping-today', null, { date: new Date(refMonth.year, refMonth.month - 1, day.date) }) : undefined}>
                  {day && (
                    <>
                      <div className="cal-date">{day.date}</div>
                      <div className="cal-orders" style={{ color: 'var(--navy)' }}>
                        {(day.ordersBase ?? day.orders)}<ReshipSup n={day.reships ?? 0} /> orders
                        {showVips && <span style={{ color: 'var(--orange)', fontWeight: 400 }}> / VIP: {day.vipOrders} ({day.vipPct}%)</span>}
                      </div>
                      <div className="cal-revenue">{day.revenue}</div>
                      {showVips && <div className="cal-vip">VIP: {day.vipRevenue} ({day.vipRevPct}%)</div>}
                      {day.vipSignups > 0 && <div className="cal-new-vip">{vipSignupLabel(day.vipSignups, day.vipNew, day.vipRejoined)}</div>}
                    </>
                  )}
                </div>
              ))}
              <div className="calendar-day cal-week-total">
                <div className="cal-date">Wk</div>
                <div className="cal-orders" style={{ color: 'var(--navy)' }}>
                  {wt.base}<ReshipSup n={wt.reships} /> orders
                  {showVips && <span style={{ color: 'var(--orange)', fontWeight: 400 }}> / VIP: {wt.vipC} ({wt.base + wt.reships ? Math.round(wt.vipC / (wt.base + wt.reships) * 100) : 0}%)</span>}
                </div>
                <div className="cal-revenue">{fmtMoney(wt.rev)}</div>
                {showVips && <div className="cal-vip">VIP: {fmtMoney(wt.vipRev)}</div>}
                {wt.vipSignups > 0 && <div className="cal-new-vip">+{wt.vipSignups} VIP</div>}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
